/* File: src/components/auth/SignupScreen.js */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider
} from 'firebase/auth';
// Import Firebase Functions callable utility and the initialized instance
import { functions as functionsInstance } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
// Import Firebase Auth instance
import { auth } from '../../firebaseConfig';
// Import constants for default Price IDs
import { PRICE_IDS } from '../../constants/stripeConstants';
// Import loading context
import { useLoading } from '../../contexts/LoadingContext';
// Import a simple loading indicator for the initial redirect check
import LoadingSpinner from '../ui/LoadingSpinner';
// --- NEW: Import useProfile to check for existing authUser ---
import { useProfile } from '../../contexts/ProfileContext';

// --- Environment Variables ---
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const VERIFY_SIGNUP_URL = process.env.REACT_APP_VERIFY_SIGNUP_URL; // URL for the NEW signup verification function

// --- CRITICAL FIX: Create DOM-based immediate overlay ---
// This is independent of React's rendering cycle and persists across navigations
const createFullscreenOverlay = () => {
    // First, check if an overlay already exists
    if (document.getElementById('immediate-checkout-overlay')) {
        return; // Prevent multiple overlays
    }

    // Create overlay element
    const overlay = document.createElement('div');
    overlay.id = 'immediate-checkout-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100vw';
    overlay.style.height = '100vh';
    overlay.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.zIndex = '9999';
    overlay.style.transition = 'opacity 0.2s ease-in-out';

    // Create logo element
    const logo = document.createElement('div');
    logo.style.width = '56px';
    logo.style.height = '56px';
    logo.style.backgroundColor = '#4f46e5';
    logo.style.borderRadius = '8px';
    logo.style.display = 'flex';
    logo.style.alignItems = 'center';
    logo.style.justifyContent = 'center';
    logo.style.color = 'white';
    logo.style.fontSize = '24px';
    logo.style.fontWeight = 'bold';
    logo.style.marginBottom = '24px';
    logo.innerText = 'N²';

    // Create message element
    const message = document.createElement('h2');
    message.style.fontSize = '20px';
    message.style.fontWeight = 'bold';
    message.style.color = '#111827';
    message.style.marginBottom = '8px';
    message.innerText = 'Redirecting to Secure Checkout';

    // Create spinner element
    const spinner = document.createElement('div');
    spinner.style.width = '40px';
    spinner.style.height = '40px';
    spinner.style.border = '3px solid rgba(79, 70, 229, 0.2)';
    spinner.style.borderRadius = '50%';
    spinner.style.borderTopColor = '#4f46e5';
    spinner.style.marginTop = '24px';
    spinner.style.animation = 'spin 1s linear infinite';

    // Create keyframes for spinner
    const style = document.createElement('style');
    style.innerHTML = `
        @keyframes spin {
            to { transform: rotate(360deg); }
        }
    `;
    document.head.appendChild(style);

    // Append elements to overlay
    overlay.appendChild(logo);
    overlay.appendChild(message);
    overlay.appendChild(spinner);

    // Add to body
    document.body.appendChild(overlay);

    // Force layout reflow to make sure everything is rendered immediately
    overlay.getBoundingClientRect();

    return overlay;
};

// Function to store checkout URL and redirect through our intermediary
const performRedirect = (checkoutUrl) => {
    // Store URL in localStorage
    localStorage.setItem('pendingCheckoutUrl', checkoutUrl);

    // Create immediate overlay to prevent flashing
    createFullscreenOverlay();

    // Use setTimeout to ensure overlay is visible before redirecting
    setTimeout(() => {
        // Redirect to checkout-redirect.html
        window.location.href = '/checkout-redirect.html';
    }, 100);
};

// --- Initialize Cloud Function Reference ---
let initiateSubscriptionCheckout = null;
try {
    if (functionsInstance) {
        initiateSubscriptionCheckout = httpsCallable(functionsInstance, 'initiateSubscriptionCheckout');
        console.log("SignupScreen: initiateSubscriptionCheckout function reference obtained.");
    } else {
        console.error("SignupScreen: Firebase Functions instance is missing from firebaseConfig. Subscription initiation will fail.");
    }
} catch (error) {
    console.error("SignupScreen: Error getting Firebase Functions reference:", error);
}
// --- Cloud Function Reference End ---


// --- Helper function for formatting EMAIL/PASSWORD signup errors ---
const getFriendlySignupErrorMessage = (errorCode) => {
    switch (errorCode) {
        case 'auth/email-already-in-use':
            return 'This email address is already registered. Please try logging in, or use Google if you originally signed up with Google.';
        case 'auth/invalid-email':
            return 'Please enter a valid email address.';
        case 'auth/weak-password':
            return 'Password is too weak. Please choose a stronger password (at least 6 characters).';
        default:
            console.error("Unhandled Firebase Signup Error Code:", errorCode);
            return 'An unexpected error occurred during signup. Please try again.';
    }
};

// --- Helper function for formatting GOOGLE SIGN-UP/IN errors ---
const getGoogleSignInErrorMessage = (errorCode) => {
     switch (errorCode) {
        case 'auth/popup-closed-by-user':
            return 'Sign-up cancelled. Please try again.';
        case 'auth/cancelled-popup-request':
            return 'Sign-up cancelled. Only one sign-up window can be open at a time.';
        case 'auth/popup-blocked':
            return 'Sign-up popup blocked by browser. Please enable popups for this site and try again.';
        case 'auth/account-exists-with-different-credential':
            return 'An account already exists with this email address using Email/Password. Please sign in using your password.';
        default:
            console.error("Unhandled Firebase Google Sign-In Error Code:", errorCode);
            return 'An unexpected error occurred during Google sign-up. Please try again.';
    }
};

/**
 * SignupScreen Component
 * Handles user registration (with reCAPTCHA), plan selection, loading state,
 * and initiating Stripe checkout.
 */
const SignupScreen = ({ onSwitchToLogin }) => {
    // --- State ---
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [googleError, setGoogleError] = useState(null);
    const [selectedPriceId, setSelectedPriceId] = useState(null);
    const [planError, setPlanError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isVerifyingPlan, setIsVerifyingPlan] = useState(true);
    const [configError, setConfigError] = useState(null); // NEW: State for config issues

    // --- Contexts ---
    const {
           showPersistentLoading, // Changed from showLoading
           hideLoading,
           updateMessage,
         } = useLoading();
    const { authUser, userHadTrial, isAuthLoading } = useProfile(); // <<<--- GET userHadTrial and isAuthLoading

    // --- Refs ---
    const isMountedRef = useRef(true);
    const componentInstanceId = useRef(`SignupScreen-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`).current;
    const redirectInProgressRef = useRef(false);

    // --- Environment Variable Checks ---
    useEffect(() => {
        let errorMsg = null;
        if (!RECAPTCHA_SITE_KEY) {
            console.error("CRITICAL: REACT_APP_RECAPTCHA_SITE_KEY environment variable is not set. reCAPTCHA verification will fail.");
            errorMsg = "Config error (RCK_SK). ";
        }
        if (!VERIFY_SIGNUP_URL) { 
             console.error("CRITICAL: REACT_APP_VERIFY_SIGNUP_URL environment variable is not set. Signup verification will fail.");
             errorMsg = (errorMsg || "") + "Config error (RCK_URL_S)."; 
        }
        if (!initiateSubscriptionCheckout) { 
             console.error("CRITICAL: initiateSubscriptionCheckout function is not available. Subscription initiation will fail.");
             errorMsg = (errorMsg || "") + "Config error (INIT_SUB).";
        }
        if (errorMsg) { setConfigError(errorMsg + " Please contact support."); }
    }, []); 

    // --- Mount/Unmount Ref Handling & Cleanup ---
    useEffect(() => {
        isMountedRef.current = true;
        console.log(`[${componentInstanceId} Mount Effect] Component mounted.`);
        const existingOverlay = document.getElementById('immediate-checkout-overlay');
        if (existingOverlay && existingOverlay.parentNode) { 
            existingOverlay.parentNode.removeChild(existingOverlay);
        }
        return () => {
            isMountedRef.current = false;
            console.log(`[${componentInstanceId} Unmount Effect] Component unmounting.`);
            if (isProcessing && !redirectInProgressRef.current) {
                console.log(`[${componentInstanceId} Unmount Effect] Hiding loading on unmount during processing.`);
                hideLoading();
            } else if (redirectInProgressRef.current) {
                console.log(`[${componentInstanceId} Unmount Effect] Keeping loading visible as redirect is in progress.`);
            }
        };
    }, [componentInstanceId, isProcessing, hideLoading]);

    // --- Get Plan ID from URL / Redirect Logic ---
    useEffect(() => {
        if (isAuthLoading) { 
             console.log(`[${componentInstanceId} Plan Check Effect] Waiting for auth state to resolve...`);
             setIsVerifyingPlan(true); 
             return;
        }
        console.log(`[${componentInstanceId} Plan Check Effect] Starting plan check. AuthUser: ${!!authUser}, Path: ${window.location.pathname}, userHadTrial: ${userHadTrial}`);
        setIsVerifyingPlan(true);
        setSelectedPriceId(null);
        setPlanError(null);

        const pendingRedirect = localStorage.getItem('pendingCheckoutUrl');
        if (pendingRedirect) {
            console.log(`[${componentInstanceId}] Found pending checkout redirect, continuing...`);
            createFullscreenOverlay();
            redirectInProgressRef.current = true;
            setTimeout(() => { window.location.href = '/checkout-redirect.html'; }, 100);
            return;
        }

        let planId = null;
        let shouldRedirectToExternalPlansPage = false;
        let redirectReason = '';
        const externalPlansPageUrl = '/plans.html';

        try {
            const params = new URLSearchParams(window.location.search);
            planId = params.get('plan');

            if (planId && (planId === PRICE_IDS.monthly || planId === PRICE_IDS.yearly)) {
                console.log(`[${componentInstanceId} Plan Check Effect] Valid plan ID detected from URL: ${planId}`);
                if (isMountedRef.current) { setSelectedPriceId(planId); }
            } else if (authUser && window.location.pathname === '/choose-plan') {
                console.log(`[${componentInstanceId} Plan Check Effect] Authenticated user on /choose-plan. Displaying plan selection UI. No specific plan pre-selected from URL.`);
            } else if (!authUser && !planId) {
                shouldRedirectToExternalPlansPage = true;
                redirectReason = 'Unauthenticated user with no plan ID specified in URL.';
            } else if (planId) { 
                shouldRedirectToExternalPlansPage = true;
                redirectReason = `Invalid plan ID ('${planId}') specified in URL.`;
            }
        } catch (e) {
             console.error(`[${componentInstanceId} Plan Check Effect] Error parsing URL params:`, e);
             shouldRedirectToExternalPlansPage = true;
             redirectReason = 'Error processing URL parameters.';
        }

        if (isMountedRef.current) { setIsVerifyingPlan(false); }

        if (shouldRedirectToExternalPlansPage) {
            console.log(`[${componentInstanceId} Plan Check Effect] Redirecting to external plans page ('${externalPlansPageUrl}'). Reason: ${redirectReason}`);
            window.location.href = externalPlansPageUrl;
            return;
        }
        console.log(`[${componentInstanceId} Plan Check Effect] Plan check complete. Selected plan (if any from URL): ${selectedPriceId}. Proceeding to render SignupScreen.`);
    }, [componentInstanceId, authUser, isAuthLoading, userHadTrial]); // <<<--- ADDED userHadTrial

    // --- Helper: Initiate Stripe Checkout ---
     const callInitiateCheckout = useCallback(async (priceId, userId) => {
        if (!initiateSubscriptionCheckout) { throw new Error("Subscription service unavailable."); }
        if (!priceId) { throw new Error("No plan selected."); }
        createFullscreenOverlay();
        redirectInProgressRef.current = true;
        showPersistentLoading("Redirecting to secure checkout...");
        console.log(`[${componentInstanceId} callInitiateCheckout] Calling initiateSubscriptionCheckout for price ID: ${priceId}`);
        try {
            const result = await initiateSubscriptionCheckout({ priceId: priceId });
            const checkoutUrl = result?.data?.checkoutUrl;
            if (checkoutUrl) {
                console.log(`[${componentInstanceId} callInitiateCheckout] Received checkout URL. Redirecting...`);
                performRedirect(checkoutUrl);
                return true;
            } else {
                console.error(`[${componentInstanceId} callInitiateCheckout] Missing checkout URL in response:`, result);
                if (isMountedRef.current) {
                     hideLoading();
                     const overlay = document.getElementById('immediate-checkout-overlay');
                     if (overlay && overlay.parentNode) { document.body.removeChild(overlay); }
                     redirectInProgressRef.current = false;
                     throw new Error("Could not start the subscription process (missing URL).");
                }
            }
        } catch (err) {
            console.error(`[${componentInstanceId} callInitiateCheckout] Error calling 'initiateSubscriptionCheckout':`, err);
            if (isMountedRef.current) {
                hideLoading();
                const overlay = document.getElementById('immediate-checkout-overlay');
                if (overlay && overlay.parentNode) { document.body.removeChild(overlay); }
                redirectInProgressRef.current = false;
                const message = err.message || "Failed to initiate checkout.";
                const details = err.details ? ` Details: ${JSON.stringify(err.details)}` : "";
                throw new Error(`${message}${details}`);
            }
        }
        return false;
    }, [componentInstanceId, hideLoading, showPersistentLoading]);


    // --- Specific Auth Functions (memoized with useCallback) ---
    const handleEmailAuth = useCallback(() => {
        console.log(`[${componentInstanceId} handleEmailAuth] Validating email/password.`);
        if (password !== confirmPassword) { throw new Error("Passwords do not match."); }
        if (password.length < 6) { throw new Error("Password must be at least 6 characters long."); }
        console.log(`[${componentInstanceId} handleEmailAuth] Validation passed. Calling createUserWithEmailAndPassword for ${email}.`);
        return createUserWithEmailAndPassword(auth, email, password);
    }, [email, password, confirmPassword, componentInstanceId]);

    const handleGoogleAuth = useCallback(() => {
        console.log(`[${componentInstanceId} handleGoogleAuth] Calling signInWithPopup.`);
        const provider = new GoogleAuthProvider();
        return signInWithPopup(auth, provider);
    }, [componentInstanceId]);

    // --- Core Signup Process (MODIFIED with reCAPTCHA & preAuthenticatedUser) ---
    const startSignupProcess = useCallback(async (authProvider, preAuthenticatedUser = null) => {
        console.log(`[${componentInstanceId} startSignupProcess] Initiated. isProcessing: ${isProcessing}, selectedPriceId: ${selectedPriceId}, isVerifyingPlan: ${isVerifyingPlan}, PreAuth: ${!!preAuthenticatedUser}`);

        if (isProcessing || !selectedPriceId || isVerifyingPlan || configError || redirectInProgressRef.current) {
            console.log(`[${componentInstanceId} startSignupProcess] Aborting: Conditions not met (Processing: ${isProcessing}, PriceID: ${selectedPriceId}, Verifying: ${isVerifyingPlan}, ConfigErr: ${!!configError}, Redirecting: ${redirectInProgressRef.current}).`);
            if (configError && isMountedRef.current) setError(configError);
            return;
        }
        if (!isMountedRef.current) {
            console.warn(`[${componentInstanceId} startSignupProcess] Aborting: Component unmounted before starting.`);
            return;
        }
        
        const isEmailAuth = authProvider === handleEmailAuth; 
        if (!preAuthenticatedUser && isEmailAuth && (typeof window.grecaptcha === 'undefined' || typeof window.grecaptcha.enterprise === 'undefined')) {
             console.error("[Signup] reCAPTCHA script not loaded or ready.");
             if(isMountedRef.current) setError("Security system failed to load. Please refresh and try again.");
             return;
        }

        setIsProcessing(true);
        showPersistentLoading(preAuthenticatedUser ? "Preparing checkout..." : (isEmailAuth ? "Verifying security..." : "Preparing signup..."));
        setError(null);
        setGoogleError(null);
        console.log(`[${componentInstanceId} startSignupProcess] State set to processing, loading screen shown.`);

        try {
             let userIdForCheckout = null;

             if (preAuthenticatedUser) {
                 console.log(`[${componentInstanceId} startSignupProcess] Using pre-authenticated user: ${preAuthenticatedUser.uid}`);
                 userIdForCheckout = preAuthenticatedUser.uid;
             } else {
                 if (isEmailAuth) {
                     await new Promise((resolve, reject) => {
                         window.grecaptcha.enterprise.ready(async () => {
                             if (!isMountedRef.current) return reject(new Error("Component unmounted during reCAPTCHA ready."));
                             try {
                                 const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: 'SIGNUP' });
                                 if (!isMountedRef.current) return reject(new Error("Component unmounted after reCAPTCHA execute."));
                                 updateMessage("Validating session...");
                                 const verifyResponse = await fetch(VERIFY_SIGNUP_URL, {
                                     method: 'POST',
                                     headers: { 'Content-Type': 'application/json' },
                                     body: JSON.stringify({ recaptchaToken: token }),
                                 });
                                 if (!isMountedRef.current) return reject(new Error("Component unmounted after backend fetch."));
                                 if (!verifyResponse.ok) {
                                     let backendErrorMsg = 'Security verification failed.';
                                     try { const errorData = await verifyResponse.json(); backendErrorMsg = errorData.error || backendErrorMsg; }
                                     catch (parseError) { backendErrorMsg = `Security check failed (Status: ${verifyResponse.status})`; }
                                     return reject(new Error(backendErrorMsg));
                                 }
                                 resolve();
                             } catch (recaptchaError) { reject(recaptchaError); }
                         });
                     });
                 }
                updateMessage("Creating account...");
                const userCredential = await authProvider(); 
                if (!userCredential?.user?.uid) {
                    throw new Error("Failed to authenticate. No user ID returned.");
                }
                userIdForCheckout = userCredential.user.uid;
                console.log(`[${componentInstanceId} startSignupProcess] Step 1: Authentication successful. UID: ${userIdForCheckout}`);
             }
            createFullscreenOverlay();
            updateMessage("Preparing checkout...");
            console.log(`[${componentInstanceId} startSignupProcess] Step 2: Initiating Stripe Checkout for price ${selectedPriceId}, user ${userIdForCheckout}...`);
            await callInitiateCheckout(selectedPriceId, userIdForCheckout);

        } catch (error) {
            console.error(`[${componentInstanceId} startSignupProcess] Error during Signup process:`, error);
            redirectInProgressRef.current = false;
            const overlay = document.getElementById('immediate-checkout-overlay');
            if (overlay && overlay.parentNode) { document.body.removeChild(overlay); }

            if (isMountedRef.current) {
                let friendlyError = "An unexpected error occurred. Please try again.";
                 if (error.message && (error.message.startsWith('Security check failed') || error.message.startsWith('Security verification failed'))) {
                     friendlyError = error.message; setError(friendlyError);
                 }
                 else if (error.code && error.code.startsWith('auth/')) {
                    friendlyError = (authProvider === handleEmailAuth || !authProvider) ? getFriendlySignupErrorMessage(error.code) : getGoogleSignInErrorMessage(error.code);
                    if (authProvider === handleEmailAuth || !authProvider) setError(friendlyError); else setGoogleError(friendlyError);
                 }
                 else if (error instanceof TypeError && error.message === 'Failed to fetch') {
                     friendlyError = "Network error verifying security. Please check connection."; setError(friendlyError);
                 }
                 else if (error.message && error.message.includes('grecaptcha')) {
                     friendlyError = "Error during security check. Please refresh and try again."; setError(friendlyError);
                 }
                 else { friendlyError = error.message || friendlyError; setError(friendlyError); }
                hideLoading();
                setIsProcessing(false);
            } else { hideLoading(); }
        }
    }, [
        isProcessing, selectedPriceId, isVerifyingPlan, configError, email, password, confirmPassword,
        callInitiateCheckout, handleEmailAuth, handleGoogleAuth,
        componentInstanceId, showPersistentLoading, hideLoading, updateMessage, authUser 
    ]);

    // --- Event Handlers ---
    const handleEmailSubmit = useCallback((e) => {
        e.preventDefault();
        console.log(`[${componentInstanceId} handleEmailSubmit] Form submitted.`);
        startSignupProcess(handleEmailAuth);
    }, [startSignupProcess, handleEmailAuth, componentInstanceId]);

    const handleGoogleSubmit = useCallback(() => {
         console.log(`[${componentInstanceId} handleGoogleSubmit] Button clicked.`);
        startSignupProcess(handleGoogleAuth);
    }, [startSignupProcess, handleGoogleAuth, componentInstanceId]);

    const handleAuthenticatedCheckout = useCallback(() => {
        if (!authUser || !selectedPriceId) {
            setError("Please select a plan first.");
            return;
        }
        console.log(`[${componentInstanceId} handleAuthenticatedCheckout] Authenticated user checkout for plan: ${selectedPriceId}`);
        startSignupProcess(null, authUser); 
    }, [authUser, selectedPriceId, startSignupProcess, componentInstanceId]);


    const navigateToLogin = useCallback(() => {
        console.log(`[${componentInstanceId} navigateToLogin] Navigate to login clicked.`);
        if (onSwitchToLogin) onSwitchToLogin();
        else console.error(`[${componentInstanceId} navigateToLogin] onSwitchToLogin prop missing!`);
    }, [onSwitchToLogin, componentInstanceId]);

    // --- Render Logic ---
    if (isAuthLoading || isVerifyingPlan) { 
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <LoadingSpinner message="Verifying plan..." />
            </div>
        );
    }

    // --- Dynamic text based on trial status ---
    const getTrialButtonText = () => {
        // For authenticated users, check their trial history
        if (authUser) {
            return userHadTrial ? "Subscribe" : "Start 14-Day Free Trial";
        }
        // For new (unauthenticated) users, it's always a trial
        return "Start 14-Day Free Trial";
    };

    const getPlanSubtitleText = (currentSelectedPriceId) => {
        const isChoosePlanPage = authUser && window.location.pathname === '/choose-plan';
        
        if (currentSelectedPriceId === PRICE_IDS.monthly) {
            return `Selected: Monthly Plan${(!authUser || (authUser && !userHadTrial)) ? " - Start 14-day free trial" : ""}`;
        }
        if (currentSelectedPriceId === PRICE_IDS.yearly) {
            return `Selected: Yearly Plan (Save ~17%)${(!authUser || (authUser && !userHadTrial)) ? " - Start 14-day free trial" : ""}`;
        }
        // When no plan is selected yet
        if (isChoosePlanPage && !isVerifyingPlan) {
            return userHadTrial ? 'Choose a plan to subscribe.' : 'Choose a plan to start your trial.';
        }
        if (!authUser && !isVerifyingPlan) return 'Select a plan to start your 14-day free trial.'; // Default for new users
        if (isVerifyingPlan) return 'Verifying plan...';
        return 'Select a plan';
    };


    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 relative">
            <div className={`bg-white p-8 rounded-lg shadow-md w-full max-w-sm transition-opacity duration-200 ${isProcessing ? 'opacity-80' : 'opacity-100'}`}>
                <div className="flex flex-col items-center mb-6">
                    <div className="h-14 w-14 mb-3 bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-lg flex items-center justify-center shadow-sm">
                        <span className="font-tomorrow font-semibold text-2xl text-white" style={{ fontFamily: 'Tomorrow, sans-serif', fontWeight: 600 }}>N²</span>
                    </div>
                    <h1 className="text-xl font-bold text-center text-gray-800 font-tomorrow" style={{ fontFamily: 'Tomorrow, sans-serif' }}>
                        Dual N-Back
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 text-center">
                        {getPlanSubtitleText(selectedPriceId)}
                    </p>
                </div>

                 {configError && (
                    <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200 text-center" role="alert">
                        {configError}
                    </p>
                 )}
                {planError && (
                    <p className="mb-4 text-sm text-orange-600 bg-orange-100 p-3 rounded border border-orange-200 text-center" role="alert">
                        {planError}
                    </p>
                )}

                {authUser ? (
                    <>
                        {!selectedPriceId && !isVerifyingPlan && (
                            <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
                                <h2 className="text-lg font-semibold text-gray-800 mb-3 text-center">
                                    {userHadTrial ? "Choose Your Plan" : "Choose Your Plan & Start Trial"}
                                </h2>
                                <div className="space-y-3">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedPriceId(PRICE_IDS.monthly)}
                                        className={`w-full flex flex-col items-center p-4 border rounded-md hover:shadow-md transition-all
                                            ${selectedPriceId === PRICE_IDS.monthly ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500' : 'border-gray-300 hover:bg-gray-100'}
                                        `}
                                        disabled={isProcessing}
                                    >
                                        <span className="text-md font-semibold text-indigo-700">Monthly Plan</span>
                                        <span className="text-xs text-gray-600 mt-1">{userHadTrial ? "Flexible monthly billing." : "Flexible monthly billing. 14-day free trial."}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedPriceId(PRICE_IDS.yearly)}
                                         className={`w-full flex flex-col items-center p-4 border rounded-md hover:shadow-md transition-all
                                            ${selectedPriceId === PRICE_IDS.yearly ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500' : 'border-gray-300 hover:bg-gray-100'}
                                        `}
                                        disabled={isProcessing}
                                    >
                                        <span className="text-md font-semibold text-indigo-700">Yearly Plan (Save ~17%)</span>
                                        <span className="text-xs text-gray-600 mt-1">{userHadTrial ? "Best value, billed annually." : "Best value, billed annually. 14-day free trial."}</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {selectedPriceId && (
                            <button
                                type="button"
                                onClick={handleAuthenticatedCheckout}
                                disabled={isProcessing || !selectedPriceId || !!configError}
                                className="py-2 px-4 mt-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                            >
                                {isProcessing ? 'Processing...' : getTrialButtonText()}
                            </button>
                        )}
                        
                        {error && !configError && (
                             <p className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200 text-center" role="alert">{error}</p>
                        )}

                        <div className="mt-6 text-sm text-center">
                            <button
                                type="button"
                                onClick={() => window.history.back()}
                                className="font-medium text-indigo-600 hover:text-indigo-500 hover:underline disabled:opacity-70 disabled:cursor-not-allowed"
                                disabled={isProcessing}
                            >
                                Cancel / Back to Account
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <form onSubmit={handleEmailSubmit} noValidate>
                            <div className="mb-4">
                                <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                <input
                                    type="email" id="signup-email" value={email} onChange={(e) => setEmail(e.target.value)} required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder="you@example.com" aria-label="Email Address" autoComplete="email"
                                    disabled={isProcessing || !selectedPriceId || !!configError}
                                />
                            </div>
                            <div className="mb-4">
                                <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                <input
                                    type="password" id="signup-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength="6"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder="•••••••• (min. 6 chars)" aria-label="Password" autoComplete="new-password"
                                    disabled={isProcessing || !selectedPriceId || !!configError}
                                />
                            </div>
                            <div className="mb-4">
                                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                                <input
                                    type="password" id="confirm-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength="6"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder="••••••••" aria-label="Confirm Password" autoComplete="new-password"
                                    disabled={isProcessing || !selectedPriceId || !!configError}
                                />
                            </div>
                            {error && !configError && (
                                <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200 text-center" role="alert">
                                    {error}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={isProcessing || !selectedPriceId || !!configError}
                                className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                            >
                                 {isProcessing ? 'Processing...' : getTrialButtonText()}
                            </button>
                        </form>

                        <div className="my-5 flex items-center">
                            <div className="flex-grow border-t border-gray-300"></div>
                            <span className="flex-shrink mx-4 text-gray-400 text-sm">OR</span>
                            <div className="flex-grow border-t border-gray-300"></div>
                        </div>

                        <button
                            type="button"
                            onClick={handleGoogleSubmit}
                            disabled={isProcessing || !selectedPriceId || !!configError}
                            className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed w-full flex items-center justify-center"
                            aria-label="Sign up with Google"
                        >
                            <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.16H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.84l3.66-2.84z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.16l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/><path d="M1 1h22v22H1z" fill="none"/></svg>
                             {isProcessing ? 'Processing...' : getTrialButtonText()}
                        </button>
                        {googleError && (
                            <p className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200 text-center" role="alert">
                                {googleError}
                            </p>
                        )}
                        <div className="mt-6 text-sm text-center">
                            <span className="text-gray-600">Already have an account?</span>{' '}
                            <button
                                type="button"
                                onClick={navigateToLogin}
                                disabled={isProcessing || !!configError}
                                className="font-medium text-indigo-600 hover:text-indigo-500 hover:underline disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                Sign in
                            </button>
                        </div>
                    </>
                )}

                 <div className="mt-6 text-center text-xs text-gray-500" style={{ visibility: 'visible' }}>
                     This site is protected by reCAPTCHA and the Google 
                     <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-gray-700">Privacy Policy</a> and 
                     <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline hover:text-gray-700">Terms of Service</a> apply.
                 </div>
            </div>
        </div>
    );
};

export default SignupScreen;